/**
 * The identity provider a login comes from. Mirrored by numeric value from the API
 * (REDLAB.SkillControl.Contracts/Primitives/IdentityProvider.cs).
 */
export enum IdentityProviderType {
  Entra = 0,
  Google = 1,
  Oidc = 2,
}

/**
 * The directory an organization signs in with: the provider and the provider's own id for it. For
 * Microsoft Entra ID that id is the tenant id; a Google Workspace is its domain and an OpenID Connect
 * issuer its URL. Tenant carries these two fields, so it passes as a directory.
 */
export interface DirectoryRef {
  identityProviderType: IdentityProviderType;
  providerTenantId?: string;
}

/**
 * Translation keys cannot be built from variables, so the provider name resolves through an
 * explicit switch. The values are product names and stay untranslated in every language.
 */
export function getIdentityProviderName(type: IdentityProviderType): string {
  switch (type) {
    case IdentityProviderType.Entra:
      return 'Microsoft Entra ID';
    case IdentityProviderType.Google:
      return 'Google Workspace';
    case IdentityProviderType.Oidc:
      return 'OpenID Connect';
    default:
      return '';
  }
}

/**
 * Whether two tenants sign in against the same directory. A tenant without a directory matches nothing,
 * not even another tenant without one.
 */
export function isSameDirectory(a: DirectoryRef | undefined, b: DirectoryRef | undefined): boolean {
  if (!a?.providerTenantId || !b?.providerTenantId) return false;

  return (
    a.identityProviderType === b.identityProviderType &&
    a.providerTenantId.toLowerCase() === b.providerTenantId.toLowerCase()
  );
}

/**
 * An allow-listed issuer as the API's login lookup (GET identity/oidc/login-info) answers it: the issuer's own
 * endpoints from its discovery document and our client id there. Kept across the redirect in session storage
 * and, for the silent sign-in of the next visit, in local storage. A plain record (not a class) because it is
 * stored and read back as JSON.
 */
export interface OidcIssuerInfo {
  issuer: string;
  clientId: string;
  displayName: string;
  authorizationEndpoint: string;
  jwksUri: string;
  endSessionEndpoint?: string;
  userInfoEndpoint?: string;
  //the issuer lists offline_access in its discovery document: the sign-in asks for it so the API gets a refresh
  //token and the session renews without a round trip through the issuer
  offlineAccessSupported: boolean;
}
